import {ErrorMessage, Formik} from 'formik'
import {cloneDeep, isArray, merge} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import Accordion from '../../../../../components/Accordion';
import BasicButton from '../../../../../components/BasicButton';
import BasicCheckbox from '../../../../../components/BasicCheckbox';
import BasicSlideToggle from '../../../../../components/BasicSlideToggle';
import BasicTitle from '../../../../../components/BasicTitle';
import FormContentLoader from '../../../../../components/FormContentLoader';
import LinkButton from '../../../../../components/LinkButton';
import {ACCOUNT_UNSUBSCRIBE_MODE} from '../../../../../constants/account';
import ENDPOINT from '../../../../../constants/endpoints';
import ERROR_FIELD from '../../../../../constants/error-fields';
import ROUTE from '../../../../../constants/route';
import {Role} from '../../../../../helpers/user';
import useApi from '../../../../../hooks/useApi';
import {useQueryParams} from '../../../../../hooks/useQueryParams';
import FormDialog from '../../../../Dialog/components/FormDialog';
import ResponseErrorDialog from '../../../../Dialog/components/ResponseErrorDialog';
import UpgradeRecruiterAccount from '../../../../Dialog/components/UpgradeRecruiterAccount';
import AccountSettingsChangeEmailForm from '../../AccountSettingsChangeEmailForm';
import EditCandidateActivityRequirementsForm from '../../EditCandidateActivityRequirementsForm';
import RecruiterAggregatedEmailScheduleForm from '../../RecruiterAggregatedEmailScheduleForm';
import {AccountSettingsFormValidationSchema} from '../validate'
import './RecruiterNotificationsSection.css'
import './styles.css'

const RecruiterNotificationsSection = props => {
  const { sections, user, history } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [checkingAlertCriteria, setCheckingAlertCriteria] = useState(false);
  const params = useQueryParams();
  const expanded = params.has('expand');

  const initData = {
    isFetching: true,
    fields: {
      AlternateEmail: '',
      ProcessDigestAlternateEmail: '',
      ReceiveMessages: 'Yes',
      IsSubscribed: 'No',
      PlatformActivity: 'No',
      ReceiveApplicationAlternate: 'No',
      ReceiveProcessDigestAlternate: 'No',
      Unsubscribe: ACCOUNT_UNSUBSCRIBE_MODE.NO,
      DailyDigest: 'No',
      WeeklyDigest: 'No',
      MonthlyNewsLetter: 'No',
      privacy: [],
      blackList: [],
      processApplic: [],
      processApplicDigest: [],
    },
    options: {
      privacy: [],
      blackList: [],
      processApplic: [],
      processApplicDigest: [],
    },
    permissions: {
      AllowedChantSettingsGoOffline: false,
      AllowedChangeEmail: false,
      AllowedChantSettingsEdit: false,
      AllowedPersonalCalendar: false,
      AllowedRecruitersProcessDigest: false,
    },
    params: {
      UnsubscribeExpireDate: null,
      UnsubscribeExpireDefaultDate: null,
    },
  };

  const api = useApi();
  const [formData, setFormData] = useState(initData);
  const [delayTimer, setDelayTimer] = useState(null);
  const [aggregatedEmailSchedule, setAggregatedEmailSchedule] = useState(null);

  const userRole = useMemo(() => ({
    isRecruiterDefault: Role.isRecruiterDefault(user),
    isCandidate: Role.isCandidate(user),
    isRecruiter: Role.isRecruiter(user),
  }), [user]);

  const checkAlertCriteria = useCallback(() => {
    setCheckingAlertCriteria(true);
    return new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.FORM.GET.ACCOUNT_CANDIDATE_ACTIVITY_ALERT_CRITERIA_CHECK,
        {},
        {
          handleError: () => {
            setCheckingAlertCriteria(false);
            reject()
          },
          handleSuccess: response => {
            setCheckingAlertCriteria(false);
            if (response.hasCriteria) {
              resolve();
            } else {
              reject();
            }
          },
        }
      );
    });
  }, [])

  const getAggregatedEmailSchedule = useCallback(() => {
    if (userRole.isRecruiter) {
      api.getRequest(
        ENDPOINT.FORM.GET.ACCOUNT_RECRUITER_AGGREGATED_EMAIL_SCHEDULE,
        {},
        {
          handleError: () => {},
          handleSuccess: response => {
            setAggregatedEmailSchedule(response.fields);
          },
        }
      );
    }
  }, []);

  const aggregatedEmailScheduleString = useMemo(() => {
    if (aggregatedEmailSchedule) {
      const week = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      let currentGroup = 0;

      if (Object.values(aggregatedEmailSchedule.days).every(day => day === 'Yes')) {
        return 'Daily';
      }

      const schedule = Object.values(aggregatedEmailSchedule.days).reduce((acc, enabled, index) => {
        if (enabled === 'Yes') {
          if (!acc[currentGroup]) {
            acc[currentGroup] = [];
          }
          acc[currentGroup].push(week[index]);
        } else if (acc[currentGroup] && acc[currentGroup].length > 0) {
          currentGroup++;
        }
        return acc;
      }, [[]]);

      return schedule.reduce((acc, group) => {
        if (group.length > 2) {
          // get first and last day
          acc.push(`${group[0]} - ${group[group.length - 1]}`);
        } else {
          acc.push(...group);
        }
        return acc;
      }, []).join(', ')
    }
    return null;
  }, [aggregatedEmailSchedule]);


  useEffect(() => {
    if (userRole.isRecruiter) {
      getAggregatedEmailSchedule();
    }
  }, [getAggregatedEmailSchedule, userRole]);

  const getSettings = useCallback(() => {
    api.get(
      ENDPOINT.FORM.GET.ACCOUNT_SETTINGS,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: formData => {
          setIsLoading(false);

          return setFormData({
            ...merge(initData, formData),
            isFetching: false,
          })
        },
      }
    );
  }, []);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  const [isDefaultRecruiter, isRecruiter] = useMemo(
    () => [
      Role.isRecruiterDefault(user),
      Role.isCandidate(user),
      Role.isRecruiter(user),
    ],
    [user]
  );

  const scrollToError = () => {
    if (document.getElementsByClassName('form__error').length > 0) {
      const elem = document.getElementsByClassName('form__error')[0];
      if (elem.previousElementSibling) {
        elem.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  };

  const onSubmit = (values, { resetForm, setSubmitting, setErrors }) => {
    if (delayTimer) {
      clearTimeout(delayTimer)
      setDelayTimer(null);
    }
    setDelayTimer(setTimeout(() => {
      let preparedValues = cloneDeep(values);

      Object.keys(preparedValues).forEach(field => {
        let data = preparedValues[field];
        if (
          preparedValues[field] === null ||
          field === 'changeEmailRequestTo' ||
          field === 'EmailAddress'
        ) {
          delete preparedValues[field];
        } else if (isArray(data) && data.length === 0) {
          delete preparedValues[field];
        }
      });

      const apiCall = new Promise((resolve, reject) => {
        api.post(ENDPOINT.FORM.SAVE.ACCOUNT_SETTINGS, preparedValues, {
          handleError: response => {
            reject(response)
            onSubmitError(response)
          },
          handleSuccess: () => {
            resolve()
            // BasicDialog({ bodyRender: <p>Your account data has been updated.</p> });
            resetForm({ ...values });
          },
          handleComplete: () => setSubmitting(false),
        });
      });

      toast.promise(apiCall, {
        pending: {
          render() {
            return <p>Saving...</p>
          },
          icon: false,
        },
        success: {
          render() {
            return <p>Your account data has been updated.</p>
          },
          // other options
          icon: "🟢",
        },
        error: {
          render({ data }) {
            const {
              data: { fields },
            } = data;
            if (fields && typeof setErrors === 'function') {
              const obj = {};
              for (let [key, value] of Object.entries(fields)) {
                const error = isArray(value) ? value[0] : value;
                obj[key] = ERROR_FIELD[error] || `${error}`;
              }
              return Object.values(obj).join('<br />')
            } else {
              // When the promise reject, data will contains the error
              return `${data.status}`
            }
          }
        }
      });
    }, 1000))
  };

  const onSubmitError = (response) => {
    ResponseErrorDialog({ response });
  };

  const handleOpenCandidateActivityRequirements = (callback) => {
    FormDialog({
      modeless: false,
      compact: true,
      className: 'alert-criteria-form-popup',
      popupInnerStyles: { marginTop: '50px' },
      params: { id: 1469, type: 'process', reqId: 41 },
      form: (actions, params) => {
        if (callback) {
          const originalProceed = actions.proceed;
          actions.proceed = (response) => {
            callback(response);
            originalProceed(response);
          }
        }
        return <EditCandidateActivityRequirementsForm
          actions={actions}
          params={params}
        />;
      },
    });
  };

  const handleChangeCheckboxBool = (field, checked, { setFieldValue }) => {
    setFieldValue(field, checked ? 'Yes' : 'No');
  };

  const handleChangeAlternateEmail = (emailValue) => {
    return FormDialog({
      title: 'Change Contact Email',
      compact: true,
      popupStyles: { minHeight: 'auto' },
      form: (actions, params) => (
        <AccountSettingsChangeEmailForm actions={actions} params={{ ...params, email: emailValue }}/>
      ),
    })
  }

  const handleChangeCheckboxArray = (
    field,
    value,
    { values, setFieldValue }
  ) => {
    const val = values[field].includes(value)
      ? values[field].filter(v => v !== value)
      : values[field].concat(value)
    setFieldValue(
      field,
      val
    );
    return val;
  };

  const handleChangeField = (field, checked, {
    value,
    values,
    emailField,
    setFieldValue,
    resetForm,
    setSubmitting,
    setErrors
  }) => {
    switch (field) {
      case 'ReceiveCandidateActivityDailyEmail':
      case 'ReceiveCandidateActivityEmail':
        if (isDefaultRecruiter) {
          UpgradeRecruiterAccount();
        } else {
          handleChangeCheckboxBool(field, checked, {
            setFieldValue,
          });
          values[field] = checked ? 'Yes' : 'No';
          onSubmit(values, { resetForm, setSubmitting, setErrors });
        }
        break;
      case 'ReceiveCandidateResumeAlerts':
      case 'ReceiveProcessDigest':
      case 'receive_applic':
        handleChangeCheckboxBool(field, checked, {
          setFieldValue,
        });
        values[field] = checked ? 'Yes' : 'No';
        onSubmit(values, { resetForm, setSubmitting, setErrors });
        break;
      case 'ProcessDigestAlternateEmail':
      case 'ReceiveProcessDigestAlternate':
      case 'ReceiveApplicationAlternate':
        if (checked) {
          handleChangeAlternateEmail(value).then(
            response => {
              setFieldValue(emailField, response.email);
              handleChangeCheckboxBool(field, checked, {
                setFieldValue,
              });
              values[emailField] = response.email;
              values[field] = response.email === '' ? 'No' : 'Yes';
              onSubmit(values, { resetForm, setSubmitting, setErrors });
            },
            response => ResponseErrorDialog({ response })
          );
        } else {
          handleChangeCheckboxBool(field, checked, {
            setFieldValue,
          });
          values[field] = checked ? 'Yes' : 'No';
          onSubmit(values, { resetForm, setSubmitting, setErrors });
        }
        break;
      case 'blackList':
        values[field] = handleChangeCheckboxArray(field, value, {
          values,
          setFieldValue,
        });
        onSubmit(values, { resetForm, setSubmitting, setErrors });
        break;
      case 'processApplic':
      case 'processApplicDigest':
        values[field] = handleChangeCheckboxArray(field, value, {
          values,
          setFieldValue,
        });
        onSubmit(values, { resetForm, setSubmitting, setErrors });
        break;
      default:
        setFieldValue(field, value);
        values[field] = value;
        onSubmit(values, { resetForm, setSubmitting, setErrors });
        break;
    }
  }

  const handleManageCalendar = () => {
    history.push(ROUTE.RECRUITER_MANAGE_CALENDAR);
  };

  const allowedPersonalCalendar = formData.permissions.AllowedPersonalCalendar;
  const AllowedRecruitersProcessDigest =
    formData.permissions.AllowedRecruitersProcessDigest;
  const allowedEmailNotifications = !Role.isGuest(user);

  return (
    <Formik
      enableReinitialize
      initialValues={formData.fields}
      validationSchema={AccountSettingsFormValidationSchema}
      onSubmit={onSubmit}
    >
      {({
        isValidating,
        values,
        handleSubmit,
        setFieldValue,
        submitForm,
        setSubmitting,
        resetForm,
        setErrors
        }) => (
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm().then(() =>
              isValidating ? scrollToError() : null
            );
          }}
          className="form__content form__content--full-width"
          autoComplete="off"
        >
          <div className="form__header"><a name="recruiter-notifications"></a>
            <h4 className="form__title">{sections['recruiter-notifications'].caption}</h4>
          </div>

          {isLoading && <FormContentLoader/>}

          {!isLoading && (allowedPersonalCalendar || allowedEmailNotifications) && allowedEmailNotifications && isRecruiter && (
            <div className="form__group">
              <Accordion label={'Candidate Activity'} isOpen={true}>
                {values.ReceiveCandidateActivityEmail && (
                  <div className="form__row">
                    <div style={{display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px'}}>
                      <BasicSlideToggle
                        value={values.ReceiveCandidateActivityEmail}
                        name="ReceiveCandidateActivityEmail"
                        labelPosition="right"
                        label={'Receive Candidate Activity Email Alerts'}
                        help={'Receive daily email with a list of candidates who meets the alert criteria'}
                        checked={checkingAlertCriteria || values.ReceiveCandidateActivityEmail === 'Yes'}
                        onChange={(field, value, checked) => {
                          const handleChangeAlertCriteria = () => {
                            handleChangeField(
                              field, checked, {
                                value,
                                values,
                                setFieldValue,
                                handleSubmit,
                                resetForm, setSubmitting, setErrors
                              }
                            )
                          };

                          if (checked) {
                            checkAlertCriteria().then(() => {
                              handleChangeAlertCriteria();
                            }).catch(() => {
                              handleOpenCandidateActivityRequirements((response) => {
                                if (response.hasCriteria) {
                                  handleChangeAlertCriteria();
                                }
                              });
                            });
                          } else {
                            handleChangeAlertCriteria();
                          }
                        }}
                        labelStyles={{
                          fontWeight: '400',
                          marginLeft: '6px',
                          marginRight: '6px',
                          textTransform: 'none'
                        }}
                        trackStyle={{marginLeft: '0'}}
                      />
                      <BasicButton
                        btnText="Set Alert Criteria"
                        btnArrow={false}
                        btnWidth={'auto'}
                        btnHeight={24}
                        style={{
                          whiteSpace: 'nowrap',
                        }}
                        controlFunc={() => {
                          if (isDefaultRecruiter) {
                            UpgradeRecruiterAccount();
                          } else {
                            handleOpenCandidateActivityRequirements();
                          }
                        }}
                      />
                    </div>

                    <ErrorMessage
                      name="ReceiveCandidateActivityEmail"
                      render={message => (
                        <div
                          className="form__error"
                          style={{
                            paddingTop: 0,
                            paddingBottom: '6px',
                          }}
                        >
                          {message}
                        </div>
                      )}
                    />
                  </div>
                )}

                {values.ReceiveCandidateActivityDailyEmail && (
                  <>
                    <div className="form__row">
                      <BasicSlideToggle
                        name="ReceiveCandidateActivityDailyEmail"
                        labelPosition="right"
                        label={'Receive Daily Candidate Log-In Information'}
                        help={'Receive daily email with a list of candidates who logged in the previous day'}
                        value={values.ReceiveCandidateActivityDailyEmail}
                        checked={
                          values.ReceiveCandidateActivityDailyEmail === 'Yes'
                        }
                        onChange={(field, value, checked) => {
                          handleChangeField(
                            field, checked, {
                              value,
                              values,
                              setFieldValue,
                              handleSubmit,
                              resetForm, setSubmitting, setErrors
                            }
                          )
                        }}
                        labelStyles={{
                          fontWeight: '400',
                          marginLeft: '6px',
                          marginRight: '6px',
                          textTransform: 'none'
                        }}
                        trackStyle={{ marginLeft: '0' }}
                      />
                    </div>
                    <div>
                      <ErrorMessage
                        name="ReceiveCandidateActivityDailyEmail"
                        render={message => (
                          <div
                            className="form__error"
                            style={{
                              paddingTop: 0,
                              paddingBottom: '6px',
                            }}
                          >
                            {message}
                          </div>
                        )}
                      />
                    </div>
                  </>
                )}

                {values.ReceiveCandidateResumeAlerts && (
                  <div className="form__row">
                    <BasicSlideToggle
                      name="ReceiveCandidateResumeAlerts"
                      labelPosition="right"
                      label={'Receive Candidate Resume Alerts'}
                      help={'Receive real-time email when the Candidate attached new resume'}
                      value={values.ReceiveCandidateResumeAlerts}
                      checked={values.ReceiveCandidateResumeAlerts === 'Yes'}
                      onChange={(field, value, checked) =>
                        handleChangeField(
                          field, checked, {
                            value,
                            values,
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                      labelStyles={{
                        fontWeight: '400',
                        marginLeft: '6px',
                        marginRight: '6px',
                        textTransform: 'none'
                      }}
                      trackStyle={{
                        marginLeft: '0'
                      }}
                    />
                  </div>
                )}
              </Accordion>

              <Accordion
                isOpen={expanded}
                label={'Periodic Application Notifications (Receive new apps in batches)'}
                footerBlocks={[
                  values.ReceiveProcessDigest === 'Yes' && (
                    <div className="form__row schedule-row">
                      <span className={'schedule-row__value'}>
                        Schedule: <strong>{aggregatedEmailScheduleString || 'Not set'}</strong>
                      </span>
                      <BasicButton
                        btnText="Change"
                        btnArrow={false}
                        btnWidth={'auto'}
                        btnHeight={24}
                        controlFunc={() => {
                          FormDialog({
                            modeless: false,
                            compact: true,
                            popupInnerStyles: { marginTop: '50px' },
                            params: {
                              id: 1469,
                              type: 'process',
                              reqId: 41,
                            },
                            form: (actions, params) => (
                              <RecruiterAggregatedEmailScheduleForm
                                actions={actions}
                                params={params}
                                onSuccessfulSubmit={() => {
                                  getAggregatedEmailSchedule();
                                }}
                              />
                            ),
                          });
                        }}
                      />
                    </div>
                  ),
                  <div className="form__row">
                    <div className={'form__column'}>
                      Send Aggregated Applications To:
                      <span className="alternate-email">
                        {values.ProcessDigestAlternateEmail || `${values.EmailAddress} [Default]`}
                      </span>
                    </div>
                    <BasicButton
                      btnText="Change"
                      btnArrow={false}
                      btnWidth={'auto'}
                      btnHeight={24}
                      controlFunc={() =>
                        handleChangeField(
                          'ReceiveProcessDigestAlternate', true, {
                            value: values.ProcessDigestAlternateEmail,
                            emailField: 'ProcessDigestAlternateEmail',
                            values,
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                    />
                  </div>
                ]}
              >
                {values.ReceiveProcessDigest && (
                  <div className="form__row">
                    <BasicSlideToggle
                      disabled={!AllowedRecruitersProcessDigest}
                      name="ReceiveProcessDigest"
                      labelPosition="right"
                      label={'Notify of New Applications (Recommended)'}
                      value={values.ReceiveProcessDigest}
                      checked={AllowedRecruitersProcessDigest && values.ReceiveProcessDigest === 'Yes'}
                      error={
                        values.ReceiveProcessDigest === 'Yes'
                        && values.processApplicDigest.length === 0
                        && 'Warning: Notifications are enabled but none will be received because no jobs are selected.'}
                      onChange={(field, value, checked) =>
                        handleChangeField(
                          field, checked, {
                            value,
                            values,
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                      labelStyles={{
                        fontWeight: '400',
                        marginLeft: '6px',
                        marginRight: '6px',
                        textTransform: 'none'
                      }}
                      trackStyle={{
                        marginLeft: '0'
                      }}
                    />
                  </div>
                )}

                {values.ReceiveProcessDigest === 'Yes' && (
                  <>
                    {formData.options.processApplicDigest.length > 0 && (
                      <div className="settings__checkbox-list">
                        {formData.options.processApplicDigest.map(process => (
                          <BasicCheckbox
                            key={process.value}
                            singleRow={true}
                            value={process.value}
                            name={'processApplicDigest'}
                            title={process.label}
                            checked={values.processApplicDigest.includes(
                              process.value
                            )}
                            onChange={(field, value, checked) =>
                              handleChangeField(
                                field, checked, {
                                  value,
                                  values,
                                  setFieldValue,
                                  handleSubmit,
                                  resetForm, setSubmitting, setErrors
                                }
                              )
                            }
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Accordion>

              <Accordion
                isOpen={expanded}
                label={'Individual Application Notifications (Receive separate email for each app)'}
                footerBlocks={[
                  <div className="form__row">
                    <div className={'form__column'}>
                      Send Individual Applications To:
                      <span className="alternate-email">
                        {values.AlternateEmail || `${values.EmailAddress} [Default]`}
                      </span>
                    </div>
                    <BasicButton
                      btnText="Change"
                      btnArrow={false}
                      btnWidth={'auto'}
                      btnHeight={24}
                      controlFunc={() =>
                        handleChangeField(
                          'ReceiveApplicationAlternate', true, {
                            value: values.AlternateEmail,
                            values,
                            emailField: 'AlternateEmail',
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                    />
                  </div>
                ]}
              >
                {values.receive_applic && (
                  <div className="form__row">
                    <BasicSlideToggle
                      name="receive_applic"
                      labelPosition="right"
                      label={'Notify Real-Time for Each Application'}
                      help={'Receive a separate email for each application [Recommended only when requiring cover letters] '}
                      error={
                        values.receive_applic === 'Yes'
                        && values.processApplic.length === 0
                        && 'Warning: Notifications are enabled but none will be received because no jobs are selected.'}
                      value={values.receive_applic}
                      checked={values.receive_applic === 'Yes'}
                      onChange={(field, value, checked) =>
                        handleChangeField(
                          field, checked, {
                            value,
                            values,
                            setFieldValue,
                            handleSubmit,
                            resetForm, setSubmitting, setErrors
                          }
                        )
                      }
                      labelStyles={{
                        fontWeight: '400',
                        marginLeft: '6px',
                        marginRight: '6px',
                        textTransform: 'none'
                      }}
                      trackStyle={{
                        marginLeft: '0'
                      }}
                    />
                  </div>
                )}

                {values.receive_applic === 'Yes' && (
                  <>
                    {formData.options.processApplic.length > 0 && (
                      <div className="settings__checkbox-list">
                        {formData.options.processApplic.map(process => (
                          <BasicCheckbox
                            key={process.value}
                            singleRow={true}
                            value={process.value}
                            name={'processApplic'}
                            title={process.label}
                            checked={values.processApplic.includes(
                              process.value
                            )}
                            onChange={(field, value, checked) =>
                              handleChangeField(
                                field, checked, {
                                  value,
                                  values,
                                  setFieldValue,
                                  handleSubmit,
                                  resetForm, setSubmitting, setErrors
                                }
                              )
                            }
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Accordion>

              {allowedPersonalCalendar && (
                <div className={'form__line'}>
                  <BasicTitle text="GOBUYSIDE RECRUITER SETTINGS" />
                  <LinkButton
                    btnFontSize="0.9286rem"
                    text="Manage Calendar"
                    btnStyles={{
                      whiteSpace: 'nowrap',
                    }}
                    controlFunc={handleManageCalendar}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      )}
    </Formik>
  )
}

export default RecruiterNotificationsSection
